import lodash from 'lodash';
import { composeReducers } from 'common/util/composeReducers';
import {
    LOAD_DATA_SOURCES,
    LOAD_DATA_SOURCE_TO_EDITOR,
    UNLOAD_DATA_SOURCE_EDITOR,
    RESET_DATA_SOURCE_EDITOR,
    DATA_SOURCE_CHANGE_METADATA_PROPERTY,
    DATA_SOURCE_CHANGE_METADATA_PARAMETER,
    DATA_SOURCE_ADD_VALUE,
    DATA_SOURCE_REMOVE_VALUES,
    DATA_SOURCE_SELECT_VALUE,
    DATA_SOURCE_SELECT_ALL,
    DATA_SOURCE_CHANGE_VALUE,
    DATA_SOURCE_DECREASE_ORDER,
    DATA_SOURCE_INCREASE_ORDER,
    DATA_SOURCE_WRITE_EDITOR_PROPERTY,
    DATA_SOURCE_ERRORS,
    DATA_SOURCE_WRITE_DATA_ITEMS
} from 'common/actions/dataSources';

export function loadDataSources(state, action) {
    if (!action || action.type !== LOAD_DATA_SOURCES) {
        return state;
    }

    const annotations = lodash.get(state, 'dataSourceEditor.annotations', action.payload.dataSourceAnnotations);

    const dataSources = lodash.chain(action.payload.dataSources)
        .map(dataSource => {
            dataSource.parameters = JSON.parse(dataSource.parameters);
            return dataSource;
        })
        .keyBy('id')
        .value();

    return {
        ...state,
        dataSources,
        dataSourceEditor: {
            ...state.dataSourceEditor,
            annotations
        }
    };
}

export function writeDataSourceToEditor(state, action) {
    if (!action || action.type !== LOAD_DATA_SOURCE_TO_EDITOR) {
        return state;
    }

    const { dataSource, descriptorDataTypes, dataSourceAnnotations } = action.payload;

    if (!dataSource) {
        return state;
    }

    const editor = { ...state.dataSourceEditor };
    editor.dataTypes = lodash.filter(descriptorDataTypes || editor.dataTypes,
        dt => dt !== 'DATASET' && dt !== 'COMPLEX_TYPE' && dt !== 'BOOLEAN');
    editor.annotations = dataSourceAnnotations || editor.annotations;
    editor.dataSource = dataSource;
    editor.dataSource.parameters = typeof dataSource.parameters === 'string'
        ? JSON.parse(dataSource.parameters)
        : dataSource.parameters;
    editor.originalDataSource = lodash.cloneDeep(dataSource);
    const values = dataSource.parameters && dataSource.parameters.values
        ? dataSource.parameters.values
        : [];
    editor.values = lodash.chain(values)
        .map(v => ({ ...v, selected: false, originalValue: { ...v } }))
        .sortBy('order')
        .value();
    editor.originalValues = lodash.map(lodash.cloneDeep(editor.values), v => { delete v.originalValue; return v; });
    editor.errors = {};

    return {
        ...state,
        dataSourceEditor: editor
    };
}

export function unloadDataSourceEditor(state, action) {
    if (!action || action.type !== UNLOAD_DATA_SOURCE_EDITOR) {
        return state;
    }
    return lodash.omit(state, 'dataSourceEditor');
}

export function resetDataSourceEditor(state, action) {
    if (!action || action.type !== RESET_DATA_SOURCE_EDITOR) {
        return state;
    }

    const { dataSourceEditor } = state;
    dataSourceEditor.dataSource = lodash.cloneDeep(dataSourceEditor.originalDataSource);
    dataSourceEditor.values = lodash.cloneDeep(dataSourceEditor.originalValues);
    dataSourceEditor.errors = {};

    return {
        ...state,
        dataSourceEditor
    };
}

export function changeMetadataProperty(state, action) {
    if (!action || action.type !== DATA_SOURCE_CHANGE_METADATA_PROPERTY) {
        return state;
    }
    const { property, value } = action.payload;
    const dataSource = { ...state.dataSourceEditor.dataSource };

    dataSource[property] = value;

    return {
        ...state,
        dataSourceEditor: {
            ...state.dataSourceEditor,
            dataSource
        }
    };
}

export function changeMetadataParameter(state, action) {
    if (!action || action.type !== DATA_SOURCE_CHANGE_METADATA_PARAMETER) {
        return state;
    }
    const { parameter, value } = action.payload;
    const parameters = { ...state.dataSourceEditor.dataSource.parameters };

    parameters[parameter] = value;

    let values = state.dataSourceEditor.values;
    if (parameter === 'dataType') {
        values = [];
    }

    return {
        ...state,
        dataSourceEditor: {
            ...state.dataSourceEditor,
            values,
            dataSource: {
                ...state.dataSourceEditor.dataSource,
                parameters
            }
        }
    };
}

export function addValue(state, action) {
    if (!action || action.type !== DATA_SOURCE_ADD_VALUE) {
        return state;
    }

    const values = [...state.dataSourceEditor.values];
    const dataType = lodash.get(state, 'dataSourceEditor.dataSource.parameters.dataType', 'small_text');
    values.push({
        selected: false,
        value: '',
        text: '',
        dataType: dataType,
        order: values.length + 1,
        status: 'active'
    });

    return {
        ...state,
        dataSourceEditor: {
            ...state.dataSourceEditor,
            values
        }
    };
}

export function removeSelectedValues(state, action) {
    if (!action || action.type !== DATA_SOURCE_REMOVE_VALUES) {
        return state;
    }

    const values = lodash(state.dataSourceEditor.values)
        .filter(v => v.selected !== true)
        .map((v, i) => ({ ...v, order: i + 1 }))
        .sortBy('order')
        .value();

    return {
        ...state,
        dataSourceEditor: {
            ...state.dataSourceEditor,
            values
        }
    };
}

export function selectValue(state, action) {
    if (!action || action.type !== DATA_SOURCE_SELECT_VALUE) {
        return state;
    }
    const { valueIndex, value: newSelected } = action.payload;
    const values = lodash.get(state, 'dataSourceEditor.values');
    const newValue = {
        ...values[valueIndex],
        selected: newSelected
    };

    return {
        ...state,
        dataSourceEditor: {
            ...state.dataSourceEditor,
            values: [
                ...values.slice(0, valueIndex),
                newValue,
                ...values.slice(valueIndex + 1)
            ]
        }
    };
}

export function selectAllValues(state, action) {
    if (!action || action.type !== DATA_SOURCE_SELECT_ALL) {
        return state;
    }

    const newSelected = action.payload;

    const values = lodash.get(state, 'dataSourceEditor.values');
    const newValues = lodash.map(values, v => ({ ...v, selected: newSelected }));

    return {
        ...state,
        dataSourceEditor: {
            ...state.dataSourceEditor,
            values: newValues
        }
    };
}

export function changeValue(state, action) {
    if (!action || action.type !== DATA_SOURCE_CHANGE_VALUE) {
        return state;
    }
    const { valueIndex, property, value: changedValue } = action.payload;
    const values = lodash.get(state, 'dataSourceEditor.values');
    const newValue = {
        ...values[valueIndex],
        [property]: changedValue
    };

    return {
        ...state,
        dataSourceEditor: {
            ...state.dataSourceEditor,
            values: [
                ...values.slice(0, valueIndex),
                newValue,
                ...values.slice(valueIndex + 1)
            ]
        }
    };
}

export function decreaseOrder(state, action) {
    if (!action || action.type !== DATA_SOURCE_DECREASE_ORDER) {
        return state;
    }

    const { valueIndex } = action.payload;
    const values = lodash.get(state, 'dataSourceEditor.values');
    const index = lodash.clamp(0, valueIndex, values.length - 1);

    if (index <= 0) {
        // element already at the lowest value
        return state;
    }

    const newValues = getNewValuesWhenChangingOrder(values, index, index - 1);
    const sortedNewValues = lodash.chain(newValues)
        .map((v, i) => ({ ...v, order: i + 1 }))
        .sortBy('order')
        .value();

    return {
        ...state,
        dataSourceEditor: {
            ...state.dataSourceEditor,
            values: sortedNewValues
        }
    };
}

export function increaseOrder(state, action) {
    if (!action || action.type !== DATA_SOURCE_INCREASE_ORDER) {
        return state;
    }

    const { valueIndex } = action.payload;

    const values = lodash.get(state, 'dataSourceEditor.values');
    const index = lodash.clamp(0, valueIndex, values.length - 1);

    if (index >= values.length - 1) {
        // element already at the highest value
        return state;
    }

    const newValues = getNewValuesWhenChangingOrder(values, index, index + 1);
    const sortedNewValues = lodash.chain(newValues)
        .map((v, i) => ({ ...v, order: i + 1 }))
        .sortBy('order')
        .value();

    return {
        ...state,
        dataSourceEditor: {
            ...state.dataSourceEditor,
            values: sortedNewValues
        }
    };
}

export function dataSourceProperty(state, action) {
    if (!action || action.type !== DATA_SOURCE_WRITE_EDITOR_PROPERTY) {
        return state;
    }
    const { property, value } = action.payload;

    return {
        ...state,
        dataSourceEditor: {
            ...state.dataSourceEditor,
            [property]: value
        }
    };
}

export function dataSourceErrors(state, action) {
    if (!action || action.type !== DATA_SOURCE_ERRORS) {
        return state;
    }

    const errors = lodash.cloneDeep(action.payload);

    return {
        ...state,
        dataSourceEditor: {
            ...state.dataSourceEditor,
            errors
        }
    };
}

export function writeDataItems(state, action) {
    if (!action || action.type !== DATA_SOURCE_WRITE_DATA_ITEMS) {
        return state;
    } 

    // 'indexDocument.selectedDescriptors' -- an object with descriptor id keys and dataset values. also
    // has dataSourceId.
    // 'indexDocument.binderIndexingMetadata.descriptorValues' -- object with descriptor id keys and lists of
    // setup values. indexDocument.binderIndexingMetadata.defaultValues,
    // and indexDocument.binderIndexingMetadata.singleValueDescriptorValues also exist.
    const selectedDescriptors = lodash.get(state, 'indexDocument.selectedDescriptors', []);
    const setupValues = lodash.get(state, 'indexDocument.binderIndexingMetadata.descriptorValues', {});
    const newState = {
        ...state,
        dataSourceItems: {
            ...state.dataSourceItems,
            ...action.payload
        },
        allowedDataSourceValues: {}
    };

    const allowedDataSourceValues = lodash.reduce(selectedDescriptors, (result, descriptor, descriptorId) => {
        result[descriptorId] = {
            descriptorId: descriptor.id,
            dataSourceId: descriptor.dataSourceId,
            allowedValues: getAllowedValues(descriptor.dataSourceId, descriptor.dataset, setupValues[descriptor.id], newState.dataSourceItems)
        };
        return result;
    }, {});

    newState.allowedDataSourceValues = allowedDataSourceValues;


    return newState;
}

function getAllowedValues(dataSourceId, dataset, setupValues, dataSourceItems) {
    let allowedValues = null;
    if (dataSourceId && setupValues) {
        allowedValues = lodash.filter(dataSourceItems[dataSourceId], (item) => {
            return lodash.includes(setupValues, item.value);
        });
    } else if (dataSourceId) {
        // if no setup values, just return all the items.
        allowedValues = dataSourceItems[dataSourceId];
    } else if (dataset) {
        allowedValues = dataset;
    }
    // if this is null, then this is a normal descriptor.
    return allowedValues;
}

function getNewValuesWhenChangingOrder(values, index, swappedIndex) {
    if (!index || !swappedIndex) {
        return values;
    }
    const newValues = lodash.clone(values);
    const value = newValues[index];
    const swappedValue = newValues[swappedIndex];
    const originalValue = value.originalValue;
    const originalSwappedValue = swappedValue.originalValue;
    const originalValueKept = originalValue && originalSwappedValue;

    if (originalValueKept) {
        newValues[index] = { ...swappedValue, originalValue: originalValue };
        newValues[swappedIndex] = { ...value, originalValue: originalSwappedValue };
    }
    else {
        newValues[index] = swappedValue;
        newValues[swappedIndex] = value;
    }
    return newValues;
}

export default composeReducers(
    loadDataSources,
    writeDataSourceToEditor,
    unloadDataSourceEditor,
    resetDataSourceEditor,
    changeMetadataProperty,
    changeMetadataParameter,
    addValue,
    removeSelectedValues,
    selectValue,
    selectAllValues,
    changeValue,
    decreaseOrder,
    increaseOrder,
    dataSourceProperty,
    dataSourceErrors,
    writeDataItems
);